import React from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import loadable from '@loadable/component';

import { propTypes } from '../../util/types';

// TODO: Clean up unused duck imports after runtime errors are resolved
// Auth duck imports
import {
  authenticationInProgress,
  logout,
} from '../../ducks/auth.duck';

// User duck imports
import {
  hasCurrentUserErrors,
  sendVerificationEmail,
} from '../../ducks/user.duck';

// UI duck imports
import { manageDisableScrolling } from '../../ducks/ui.duck';

// Payment Methods duck - Sonispark uses credits, not real payments

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

export const TopbarContainerComponent = ({
  currentPage = null,
  currentSearchParams = null,
  currentUser = null,
  currentUserHasOrders = null,
  notificationCount = 0,
  sendVerificationEmailError = null,
  authScopes = null,
  authInProgress,
  currentUserHasListings,
  isAuthenticated,
  hasGenericError,
  onLogout,
  onManageDisableScrolling,
  sendVerificationEmailInProgress,
  onResendVerificationEmail,
  title,
  ...rest
}) => {
  // React Router v7: Use hooks instead of withRouter HOC
  const location = useLocation();
  const navigate = useNavigate();
  // Create history-like object for backwards compatibility
  const history = {
    push: (path, state) => navigate(path, { state }),
    replace: (path, state) => navigate(path, { state, replace: true }),
  };

  return (
    <Topbar
      authInProgress={authInProgress}
      currentPage={currentPage}
      currentSearchParams={currentSearchParams}
      currentUser={currentUser}
      currentUserHasListings={currentUserHasListings}
      currentUserHasOrders={currentUserHasOrders}
      history={history}
      isAuthenticated={isAuthenticated}
      authScopes={authScopes}
      location={location}
      notificationCount={notificationCount}
      onLogout={onLogout}
      onManageDisableScrolling={onManageDisableScrolling}
      onResendVerificationEmail={onResendVerificationEmail}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      showGenericError={hasGenericError}
      title={title}
      {...rest}
    />
  );
};



TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated
  const { isAuthenticated, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserProfileListing,
    currentUserNotificationCount: notificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserProfileListing,
    currentUserHasOrders,
    notificationCount,
    isAuthenticated,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
});

// React Router v6+ no longer requires withRouter
const TopbarContainer = compose(
  connect(mapStateToProps, mapDispatchToProps)
)(TopbarContainerComponent);

export default TopbarContainer;
